// 8-Point Grid Spacing System for WhatsApp Portal
// Provides consistent, scalable spacing throughout the application

// Base unit: 8px
const BASE_UNIT = 8;

// Spacing scale using 8-point grid system
export const spacing = {
  // Individual spacing values
  0: '0px',
  1: '4px',    // 0.5 * BASE_UNIT - for micro adjustments
  2: '8px',    // 1 * BASE_UNIT - minimum spacing
  3: '12px',   // 1.5 * BASE_UNIT - small spacing
  4: '16px',   // 2 * BASE_UNIT - standard spacing
  5: '20px',   // 2.5 * BASE_UNIT - medium spacing
  6: '24px',   // 3 * BASE_UNIT - large spacing
  7: '28px',   // 3.5 * BASE_UNIT - extra large spacing
  8: '32px',   // 4 * BASE_UNIT - section spacing
  10: '40px',  // 5 * BASE_UNIT - major spacing
  12: '48px',  // 6 * BASE_UNIT - layout spacing
  16: '64px',  // 8 * BASE_UNIT - hero spacing
  20: '80px',  // 10 * BASE_UNIT - dramatic spacing
  24: '96px',  // 12 * BASE_UNIT - page spacing
  32: '128px', // 16 * BASE_UNIT - large layout spacing
  40: '160px', // 20 * BASE_UNIT - extra large layout spacing
  48: '192px', // 24 * BASE_UNIT - maximum spacing
};

// Component-specific spacing standards
export const componentSpacing = {
  // Button spacing
  button: {
    paddingHorizontal: spacing[6], // 24px
    paddingVertical: spacing[3],   // 12px
    gap: spacing[2],               // 8px between icon and text
    groupGap: spacing[4],          // 16px between buttons
  },
  
  // Card spacing
  card: {
    padding: spacing[6],           // 24px internal padding
    margin: spacing[4],            // 16px between cards
    headerPadding: spacing[4],     // 16px header padding
    contentPadding: spacing[6],    // 24px content padding
    actionsPadding: spacing[4],    // 16px actions padding
  },
  
  // Form spacing
  form: {
    fieldGap: spacing[4],          // 16px between form fields
    groupGap: spacing[8],          // 32px between form groups
    labelGap: spacing[2],          // 8px between label and input
    helperGap: spacing[1],         // 4px between input and helper text
    sectionGap: spacing[12],       // 48px between form sections
  },
  
  // List spacing
  list: {
    itemPadding: spacing[4],       // 16px list item padding
    itemGap: spacing[2],           // 8px between list items
    groupGap: spacing[6],          // 24px between list groups
    iconGap: spacing[3],           // 12px between icon and text
  },
  
  // Navigation spacing
  navigation: {
    itemPadding: spacing[4],       // 16px nav item padding
    itemGap: spacing[2],           // 8px between nav items
    groupGap: spacing[8],          // 32px between nav groups
    submenuIndent: spacing[6],     // 24px submenu indentation
  },
  
  // Modal/Dialog spacing
  modal: {
    padding: spacing[6],           // 24px modal padding
    headerPadding: spacing[6],     // 24px header padding
    contentPadding: spacing[6],    // 24px content padding
    actionsPadding: spacing[4],    // 16px actions padding
    overlay: spacing[4],           // 16px from screen edges
  },
  
  // Table spacing
  table: {
    cellPadding: spacing[4],       // 16px cell padding
    headerPadding: spacing[4],     // 16px header padding
    rowGap: spacing[1],            // 4px between rows (subtle)
    sectionGap: spacing[6],        // 24px between table sections
  },
  
  // Page layout spacing
  layout: {
    containerPadding: spacing[6],  // 24px container padding
    sectionGap: spacing[12],       // 48px between page sections
    contentGap: spacing[8],        // 32px between content blocks
    headerGap: spacing[6],         // 24px below page headers
    footerGap: spacing[8],         // 32px above page footers
  },
  
  // Sidebar spacing
  sidebar: {
    padding: spacing[4],           // 16px sidebar padding
    itemPadding: spacing[3],       // 12px sidebar item padding
    groupGap: spacing[6],          // 24px between sidebar groups
    titleGap: spacing[2],          // 8px below sidebar titles
  },
};

// Responsive spacing adjustments
export const responsiveSpacing = {
  mobile: {
    scale: 0.75,                   // 75% of desktop spacing
    containerPadding: spacing[4],  // 16px container padding
    sectionGap: spacing[8],        // 32px between sections
    contentGap: spacing[6],        // 24px between content
  },
  
  tablet: {
    scale: 0.875,                  // 87.5% of desktop spacing
    containerPadding: spacing[5],  // 20px container padding
    sectionGap: spacing[10],       // 40px between sections
    contentGap: spacing[7],        // 28px between content
  },
  
  desktop: {
    scale: 1,                      // 100% - base spacing
    containerPadding: spacing[6],  // 24px container padding
    sectionGap: spacing[12],       // 48px between sections
    contentGap: spacing[8],        // 32px between content
  },
  
  wide: {
    scale: 1.125,                  // 112.5% for wide screens
    containerPadding: spacing[8],  // 32px container padding
    sectionGap: spacing[16],       // 64px between sections
    contentGap: spacing[10],       // 40px between content
  },
};

// Spacing utility functions
export const spacingUtils = {
  // Convert spacing key to value
  getValue: (key) => spacing[key] || spacing[4], // Default to 16px
  
  // Get responsive spacing
  getResponsive: (key, breakpoint = 'desktop') => {
    const baseValue = parseInt(spacing[key]);
    const scale = responsiveSpacing[breakpoint]?.scale || 1;
    return `${Math.round(baseValue * scale)}px`;
  },
  
  // Generate spacing CSS
  generateCss: (property, value, responsive = false) => {
    if (responsive) {
      return {
        [property]: spacingUtils.getResponsive(value, 'mobile'),
        '@media (min-width: 768px)': {
          [property]: spacingUtils.getResponsive(value, 'tablet'),
        },
        '@media (min-width: 1024px)': {
          [property]: spacingUtils.getResponsive(value, 'desktop'),
        },
        '@media (min-width: 1440px)': {
          [property]: spacingUtils.getResponsive(value, 'wide'),
        },
      };
    }
    return { [property]: spacing[value] };
  },
  
  // Helper for margin/padding shorthand
  getSpacingShorthand: (top, right, bottom, left) => {
    const values = [top, right, bottom, left]
      .map(key => spacing[key] || spacing[0])
      .join(' ');
    return values;
  },
};

// Spacing guidelines and best practices
export const spacingGuidelines = {
  principles: {
    title: 'Spacing Principles',
    rules: [
      'Use multiples of 8px for all spacing to maintain consistency',
      'Smaller spacing for related elements, larger for unrelated elements',
      'Maintain consistent spacing within similar components',
      'Use white space strategically to improve readability and focus',
    ],
  },
  
  hierarchy: {
    title: 'Spacing Hierarchy',
    levels: [
      '4px (spacing[1]): Micro adjustments, tight relationships',
      '8px (spacing[2]): Minimum spacing, very close relationships',
      '16px (spacing[4]): Standard spacing, related elements',
      '24px (spacing[6]): Section spacing, moderately related elements',
      '32px (spacing[8]): Large spacing, different sections',
      '48px (spacing[12]): Major spacing, distinct sections',
      '64px+ (spacing[16]+): Dramatic spacing, major layout divisions',
    ],
  },
  
  responsive: {
    title: 'Responsive Spacing',
    guidelines: [
      'Reduce spacing on smaller screens to maximize content area',
      'Maintain proportional relationships across breakpoints',
      'Ensure touch targets remain accessible (minimum 44px)',
      'Test spacing at different screen sizes and orientations',
    ],
  },
  
  accessibility: {
    title: 'Accessibility Considerations',
    requirements: [
      'Minimum 44px touch target size on mobile devices',
      'Sufficient spacing between interactive elements',
      'Clear visual separation between different content sections',
      'Consistent spacing patterns for screen reader navigation',
    ],
  },
  
  implementation: {
    title: 'Implementation Tips',
    tips: [
      'Use CSS custom properties for spacing values',
      'Create reusable spacing utility classes',
      'Document spacing decisions in design tokens',
      'Validate spacing consistency in design reviews',
    ],
  },
};

// CSS custom properties for spacing (to be used in global styles)
export const spacingCssVariables = Object.entries(spacing).reduce((acc, [key, value]) => {
  acc[`--spacing-${key}`] = value;
  return acc;
}, {});

// Material-UI spacing function compatible values
export const muiSpacingFunction = (factor) => {
  if (typeof factor === 'number') {
    return `${BASE_UNIT * factor}px`;
  }
  if (Array.isArray(factor)) {
    return factor.map(f => `${BASE_UNIT * f}px`).join(' ');
  }
  return spacing[factor] || `${BASE_UNIT * factor}px`;
};

export default {
  spacing,
  componentSpacing,
  responsiveSpacing,
  spacingUtils,
  spacingGuidelines,
  spacingCssVariables,
  muiSpacingFunction,
};